import api from './api';
import { tokenManager } from '../contexts/AuthContext';

const AUTH_ENDPOINTS = {
  LINE_CALLBACK: '/auth/line/callback/',
  ME: '/auth/me/',
  REFRESH: '/auth/refresh/',
  LOGOUT: '/auth/logout/',
  MERGE_BY_PHONE: '/auth/merge-by-phone/'
};

class AuthService {
  /**
   * Generate LINE authorization URL
   * @param {string} merchantSlug - Optional merchant slug for state parameter
   * @returns {string} LINE authorization URL
   */
  generateLineAuthUrl(merchantSlug = null) {
    const currentHost = window.location.hostname;
    const isLocal = currentHost === 'localhost' || currentHost === '127.0.0.1';
    
    // 在本地開發時使用 localhost:8040，在生產環境時使用 /api（會被 Firebase 代理到後端）
    const apiUrl = isLocal 
      ? (process.env.REACT_APP_API_URL || 'http://localhost:8040/api')
      : '/api';

    // 如果 API_URL 是相對路徑（生產環境），使用當前域名 + /api
    // 如果 API_URL 是完整 URL（本地開發），直接使用
    let redirectUri;
    if (apiUrl.startsWith('http')) {
      // 本地開發：http://localhost:8040/api/auth/line/callback/
      redirectUri = `${apiUrl}${AUTH_ENDPOINTS.LINE_CALLBACK}`;
    } else {
      // 生產環境：https://app.kakatw.com/api/auth/line/callback/
      // window.location.origin 會拿到 https://app.kakatw.com
      const frontendUrl = window.location.origin;
      redirectUri = `${frontendUrl}${apiUrl}${AUTH_ENDPOINTS.LINE_CALLBACK}`;
    }

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: process.env.REACT_APP_LINE_CLIENT_ID,
      redirect_uri: redirectUri,
      state: merchantSlug || 'default',
      scope: 'openid profile'
    });

    return `https://access.line.me/oauth2/v2.1/authorize?${params.toString()}`;
  }

  /**
   * Get current user information
   * @returns {Promise} User data
   */
  async getCurrentUser() {
    try {
      const response = await api.get(AUTH_ENDPOINTS.ME);
      return response.data;
    } catch (error) {
      console.error('Get current user error:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Refresh authentication token
   * @returns {Promise} New token data
   */
  async refreshToken() {
    try {
      const currentToken = tokenManager.getToken();
      if (!currentToken) {
        throw new Error('No token available for refresh');
      }

      const response = await api.post(AUTH_ENDPOINTS.REFRESH, {
        token: currentToken
      });

      return response.data;
    } catch (error) {
      console.error('Token refresh error:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Logout user
   * @returns {Promise} Logout response
   */
  async logout() {
    try {
      const response = await api.post(AUTH_ENDPOINTS.LOGOUT);
      return response.data;
    } catch (error) {
      console.error('Logout error:', error);
      // Don't throw error for logout, just log it
      return null;
    }
  }

  /**
   * Merge user accounts by phone number
   * @param {string} phoneNumber - Phone number to merge with
   * @returns {Promise} Merge result
   */
  async mergeByPhone(phoneNumber) {
    try {
      const response = await api.post(AUTH_ENDPOINTS.MERGE_BY_PHONE, {
        phone_number: phoneNumber
      });
      return response.data;
    } catch (error) {
      console.error('Merge by phone error:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Verify phone number for account merge
   * @param {string} phoneNumber - Phone number to verify
   * @param {string} verificationCode - Verification code
   * @returns {Promise} Verification result
   */
  async verifyPhoneForMerge(phoneNumber, verificationCode) {
    try {
      const response = await api.post('/auth/verify-phone-merge/', {
        phone_number: phoneNumber,
        verification_code: verificationCode
      });
      return response.data;
    } catch (error) {
      console.error('Phone verification for merge error:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Confirm account merge with token
   * @param {string} mergeToken - Merge token from verification
   * @returns {Promise} Merge confirmation result
   */
  async confirmAccountMerge(mergeToken) {
    try {
      const response = await api.post('/auth/confirm-merge/', {
        merge_token: mergeToken
      });
      return response.data;
    } catch (error) {
      console.error('Confirm account merge error:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Update user profile
   * @param {Object} profileData - Profile data to update
   * @returns {Promise} Updated user data
   */
  async updateProfile(profileData) {
    try {
      const response = await api.put('/users/profile/', profileData);
      return response.data;
    } catch (error) {
      console.error('Update profile error:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Get user booking history
   * @param {string} userId - User ID
   * @param {Object} params - Query parameters (page, limit, etc.)
   * @returns {Promise} Booking history data
   */
  async getUserBookings(userId, params = {}) {
    try {
      const response = await api.get(`/users/${userId}/bookings/`, { params });
      return response.data;
    } catch (error) {
      console.error('Get user bookings error:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Get user payment history
   * @param {string} userId - User ID
   * @param {Object} params - Query parameters (page, limit, etc.)
   * @returns {Promise} Payment history data
   */
  async getUserPayments(userId, params = {}) {
    try {
      const response = await api.get(`/users/${userId}/payments/`, { params });
      return response.data;
    } catch (error) {
      console.error('Get user payments error:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Handle authentication errors
   * @param {Error} error - The error object
   * @returns {Error} Formatted error
   */
  handleAuthError(error) {
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          return new Error('Authentication failed. Please login again.');
        case 403:
          return new Error('Access denied. You do not have permission to perform this action.');
        case 404:
          return new Error('Resource not found.');
        case 422:
          return new Error(data.message || 'Invalid data provided.');
        case 500:
          return new Error('Server error. Please try again later.');
        default:
          return new Error(data.message || 'An unexpected error occurred.');
      }
    } else if (error.request) {
      return new Error('Network error. Please check your connection.');
    } else {
      return new Error(error.message || 'An unexpected error occurred.');
    }
  }

  /**
   * Check if error is authentication related
   * @param {Error} error - The error object
   * @returns {boolean} True if auth error
   */
  isAuthError(error) {
    return error.response && (error.response.status === 401 || error.response.status === 403);
  }

  /**
   * Validate LINE client configuration
   * @returns {boolean} True if configuration is valid
   */
  validateLineConfig() {
    const clientId = process.env.REACT_APP_LINE_CLIENT_ID;
    const frontendUrl = process.env.REACT_APP_LINE_FRONTEND_URL;

    if (!clientId) {
      console.error('REACT_APP_LINE_CLIENT_ID is not configured');
      return false;
    }

    if (!frontendUrl) {
      console.warn('REACT_APP_LINE_FRONTEND_URL is not configured, using default');
    }

    return true;
  }
}

export default new AuthService();